import { Component } from "kernelplay-js";
import * as PIXI from "pixi.js";

export class PixiSpriteComponent extends Component {
    constructor(props = {}) {
        super();

        const {
            image = null,
            width = 50,
            height = 50,
            sourceX = 0,
            sourceY = 0,
            sourceWidth = null,
            sourceHeight = null,
            anchor = { x: 0.5, y: 0.5 },
            flipX = false,
            flipY = false,
            tint = 0xffffff,
            alpha = 1.0,
            zIndex = 0,
        } = props;

        this.image = image;
        this.width = width;
        this.height = height;
        this.sourceX = sourceX;
        this.sourceY = sourceY;
        this.sourceWidth = sourceWidth;
        this.sourceHeight = sourceHeight;
        this.anchor = anchor;
        this.flipX = flipX;
        this.flipY = flipY;
        this.tint = tint;
        this.alpha = alpha;
        this.zIndex = zIndex;

        // Internal
        this.object = null;  // PIXI.Sprite — used by WebGL2DRenderer
        this.PixiSprite = null;
        this._texture = null;
        this._loaded = false;
        this._baseTexture = null;
    }

    init() {
        this.transform = this.entity.getComponent("transform");

        if (!this.image) {
            // No image — create a colored rectangle placeholder
            this._createPlaceholder();
            return;
        }

        this._loadTexture();
    }

    // ── Texture loading ───────────────────────────────────────────────────

    _loadTexture() {
        // Check cache first — if already loaded, use synchronously
        const cached = PIXI.Assets.cache.get(this.image);
        if (cached) {
            this._onTextureLoaded(cached);
            return;
        }
        
        PIXI.Assets.load(this.image).then((texture) => {
            this._onTextureLoaded(texture);
        }).catch(() => {
            console.error(`PixiSpriteComponent: failed to load image "${this.image}"`);
            this._createPlaceholder();
        });
    }

    _onTextureLoaded(fullTexture) {
        const sw = this.sourceWidth ?? fullTexture.width;
        const sh = this.sourceHeight ?? fullTexture.height;

        this._baseTexture = fullTexture.source; // ← v8 uses .source not .baseTexture

        this._texture = new PIXI.Texture({
            source: fullTexture.source,
            frame: new PIXI.Rectangle(this.sourceX, this.sourceY, sw, sh),
            orig: new PIXI.Rectangle(0, 0, this.width, this.height)
        });

        this._createSprite();
        this._loaded = true;
    }

    _createSprite() {
        this.object = new PIXI.Sprite(this._texture);
        this.PixiSprite = this.object;

        // this.object.width = this.width;
        // this.object.height = this.height;

        // const texW = this._texture.width;
        // const texH = this._texture.height;

        // this.object.scale.set(
        //     (this.width / texW) * (this.flipX ? -1 : 1),
        //     (this.height / texH) * (this.flipY ? -1 : 1)
        // );
        

        this.object.anchor.set(this.anchor.x, this.anchor.y);
        this.object.tint = this.tint;
        this.object.alpha = this.alpha;
        this.object.zIndex = this.zIndex;
        

        // this._applyFlip();

        // Add to stage via scene
        // const stage = this.entity.scene?.game?.renderer?.stage;
        // const stage = this.entity.scene.game.renderer.stage;
        // const stage = this.entity.scene?.game?.renderer?.stage;
        // if (stage) {
        //     stage.addChild(this.object);
        //     stage.sortableChildren = true;
        // }
    }

    _createPlaceholder() {
        // Colored box — useful for debugging or untextured entities
        const gfx = new PIXI.Graphics()
            .rect(-this.width * this.anchor.x, -this.height * this.anchor.y, this.width, this.height)
            .fill(0x885588);

        this.object = gfx;
        this.object.alpha = this.alpha;
        this.object.zIndex = this.zIndex;

        // const stage = this.entity.scene?.game?.renderer?.stage;
        // if (stage) {
        //     stage.addChild(this.object);
        //     stage.sortableChildren = true;
        // }

        this._loaded = true;
    }

    onStart() {
        const stage = this.entity.scene?.game?.renderer?.app?.stage;
        if (stage) {
            stage.addChild(this.object);
            stage.sortableChildren = true;
        }
    }

    // ── Public API ────────────────────────────────────────────────────────

    /** Change the visible source rect — used by AnimatorComponent */
    setFrame(x, y, width, height) {
        this.sourceX = x;
        this.sourceY = y;
        if (width !== undefined) this.sourceWidth = width;
        if (height !== undefined) this.sourceHeight = height;

        if (!this._texture || !this.object) return;

        // ← v8 way to update frame
        this._texture.frame = new PIXI.Rectangle(
            this.sourceX,
            this.sourceY,
            this.sourceWidth ?? this.width,
            this.sourceHeight ?? this.height
        );
        this._texture.updateUvs();
    }

    set flipX(value) {
        this._flipX = value;
        this._applyFlip();
    }
    get flipX() { return this._flipX ?? false; }

    set flipY(value) {
        this._flipY = value;
        this._applyFlip();
    }
    get flipY() { return this._flipY ?? false; }

    _applyFlip() {
        if (!this.object) return;
        this.object.scale.x = Math.abs(this.object.scale.x) * (this._flipX ? -1 : 1);
        this.object.scale.y = Math.abs(this.object.scale.y) * (this._flipY ? -1 : 1);
    }

    set alpha(value) {
        this._alpha = value;
        if (this.object) this.object.alpha = value;
    }
    get alpha() { return this._alpha ?? 1.0; }

    set tint(value) {
        this._tint = value;
        if (this.object) this.object.tint = value;
    }
    get tint() { return this._tint ?? 0xffffff; }

    set zIndex(value) {
        this._zIndex = value;
        if (this.object) this.object.zIndex = value;
    }
    get zIndex() { return this._zIndex ?? 0; }

    // ── Bounds — used by frustum culling ─────────────────────────────────

    getBounds() {
        const t = this.transform;
        if (!t) return null;

        const w = this.width * t.scale.x;
        const h = this.height * t.scale.y;

        return {
            x: t.position.x - w * this.anchor.x,
            y: t.position.y - h * this.anchor.y,
            width: w,
            height: h,
        };
    }

    // ── Cleanup ───────────────────────────────────────────────────────────

    onDestroy() {
        if (this.object) {
            this.object.parent?.removeChild(this.object);
            this.object.destroy({ texture: false }); // don't destroy shared texture
            this.object = null;
        }
    }

    // ── Serialization ─────────────────────────────────────────────────────

    toJSON() {
        return {
            type: "PixiSpriteComponent",
            image: this.image,
            width: this.width,
            height: this.height,
            sourceX: this.sourceX,
            sourceY: this.sourceY,
            sourceWidth: this.sourceWidth,
            sourceHeight: this.sourceHeight,
            anchor: this.anchor,
            flipX: this.flipX,
            flipY: this.flipY,
            tint: this.tint,
            alpha: this.alpha,
            zIndex: this.zIndex,
        };
    }

    static fromJSON(data) {
        return new PixiSpriteComponent(data);
    }
}